import { CodeOutlined, DatabaseOutlined, EyeOutlined, FileSearchOutlined, HomeOutlined, SearchOutlined, ShareAltOutlined } from "@ant-design/icons";
import { faGear, faHeartPulse, faKey, faListCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NovuInbox from "components/atoms/NovuInbox";
import HeaderNavigationBar from "components/molecules/HeaderNavigationBar";
import DashboardIcon from "components/molecules/HeaderNavigationBar/DashboardIcon";
import AppPaths from "constants/appPaths";
import BusinessAreaSelectMenu from "containers/BusinessAreaSelectMenu";
import UserProfileMenu from "containers/UserProfileMenu";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import actions from "store/actions";
import CypressTestIds from "../../../cypress/CypressTestIds";

export default function HeaderMenu({ companyName = "Pretectum" }) {
    const dispatch = useAppDispatch();
    const tenant = useAppSelector((state) => state.userLogin.tenant);
    const user = useAppSelector((state) => state.userLogin.user);
    const userBusinessAreas = useAppSelector((state) => state.home.businessAreas);
    const activeBusinessAreaId = useAppSelector((state) => state.home.activeBusinessAreaId);
    const customizationData = useAppSelector((state) => state.tenancyCustomization.customizationData);
    const location = useLocation();
    const history = useHistory();
    const userLogout = () => dispatch(actions.userLogoutRequest());

    const logoUrl = customizationData?.logo || "/icons/header-menu/pretectum-logo.png";
    const logoSize = customizationData?.logoSize || { height: "32px", width: "120px" };

    const getActiveBusinessAreaName = React.useCallback(() => {
        if (userBusinessAreas) {
            let activeBusinessArea = userBusinessAreas.find((businessArea) => businessArea.businessAreaId === activeBusinessAreaId);
            if (activeBusinessArea) {
                return activeBusinessArea.name.toLowerCase();
            }
        }
        return "default";
    }, [activeBusinessAreaId, userBusinessAreas]);

    return (
        <HeaderNavigationBar
            logo={logoUrl}
            logoDimensions={logoSize}
            companyName={companyName}
            colors={{
                colorNavigationBar: customizationData?.colorNavigationBar,
                colorNavigationFont: customizationData?.colorNavigationFont,
                colorSelectedHeader: customizationData?.colorSelectedHeader,
            }}
            businessAreaSelectMenu={<BusinessAreaSelectMenu />}
            userProfileMenu={<UserProfileMenu onLogout={userLogout} />}
            leftItems={[
                {
                    title: "Home",
                    icon: <HomeOutlined />,
                    hideTitle: user && user.isGlobalAdmin,
                    active: location.pathname.split("/").pop() === "home",
                    onClick: () => tenant && history.push(AppPaths.TENANT_HOME.replace(":tenant", tenant.name)),
                    "data-testid": CypressTestIds.HEADER_MENU_HOME,
                },
                ...(user && user.isGlobalAdmin
                    ? [
                          {
                              title: "Admin Console",
                              icon: <CodeOutlined />,
                              active: location.pathname.split("/").pop() === "adminconsole",
                              onClick: () => history.push(AppPaths.ADMIN_CONSOLE),
                          },
                      ]
                    : []),
                {
                    title: "Domain Console",
                    icon: <DashboardIcon />,
                    active: location.pathname.split("/").pop() === "domainserviceconsole",
                    onClick: () => tenant && history.push(AppPaths.TENANT_DOMAIN_SERVICE_CONSOLE.replace(":tenant", tenant.name)),
                },
                {
                    title: "Schema",
                    icon: <DatabaseOutlined />,
                    active: location.pathname.split("/").pop() === "schemas",
                    onClick: () =>
                        tenant &&
                        activeBusinessAreaId &&
                        history.push(AppPaths.TENANT_BUSINESS_AREA_SCHEMA.replace(":tenant", tenant.name).replace(":businessarea", getActiveBusinessAreaName())),
                    "data-testid": CypressTestIds.HEADER_MENU_SCHEMA,
                },
                {
                    title: "Relationships",
                    icon: <ShareAltOutlined />,
                    active: location.pathname.split("/").pop() === "lineage",
                    onClick: () => tenant && history.push(AppPaths.TENANT_LINEAGE.replace(":tenant", tenant.name)),
                    "data-testid": CypressTestIds.HEADER_MENU_RELATIONSHIPS,
                },
                {
                    title: "Views",
                    icon: <EyeOutlined />,
                    active: location.pathname.includes("/views"),
                    onClick: () => tenant && history.push(AppPaths.TENANT_VIEWS.replace(":tenant", tenant.name)),
                    "data-testid": CypressTestIds.HEADER_MENU_VIEWS,
                },
                {
                    title: "Search",
                    icon: <SearchOutlined />,
                    active: location.pathname.split("/").pop() === "search",
                    onClick: () => tenant && history.push(AppPaths.TENANT_DATA_OBJECT_SEARCH.replace(":tenant", tenant.name)),
                    "data-testid": CypressTestIds.HEADER_MENU_SEARCH,
                },
                {
                    title: "Search Duplicates",
                    icon: <FileSearchOutlined />,
                    active: location.pathname.split("/").pop() === "duplicates",
                    onClick: () => tenant && history.push(AppPaths.TENANT_DATA_OBJECT_DUPLICATE_SEARCH.replace(":tenant", tenant.name)),
                    "data-testid": CypressTestIds.HEADER_MENU_SEARCH_DUPLICATE,
                },
            ]}
            rightItems={[
                {
                    title: "Tasks",
                    icon: <FontAwesomeIcon icon={faListCheck} />,
                    hideTitle: true,
                    active: location.pathname.split("/").pop() === "mytasks",
                    onClick: () => tenant && history.push(AppPaths.TENANT_MY_TASKS.replace(":tenant", tenant.name)),
                },
                {
                    title: "Inbox",
                    icon: <NovuInbox />,
                    hideTitle: true,
                    className: "!px-[9px]",
                },
                {
                    title: "Configurations",
                    icon: <FontAwesomeIcon icon={faGear} />,
                    hideTitle: true,
                    active: location.pathname.split("/").pop() === "configuration",
                    onClick: () => tenant && history.push(AppPaths.TENANT_CONFIGURATION.replace(":tenant", tenant.name)),
                    "data-testid": CypressTestIds.HEADER_MENU_CONFIGURATION,
                },
                {
                    title: "Jobs and Events",
                    icon: <FontAwesomeIcon icon={faHeartPulse} />,
                    hideTitle: true,
                    active: location.pathname.split("/").pop() === "jobs",
                    onClick: () => tenant && history.push(AppPaths.TENANT_MONITOR_ITEM.replace(":tenant", tenant.name).replace(":monitorItem", "jobs")),
                },
                {
                    title: "License Info",
                    icon: <FontAwesomeIcon icon={faKey} />,
                    hideTitle: true,
                    active: location.pathname.split("/").pop() === "licenseinfo",
                    onClick: () => tenant && history.push(AppPaths.TENANT_LICENSE_INFO.replace(":tenant", tenant.name)),
                },
            ]}
        />
    );
}
